import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRightIcon,
  BoltIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
  SparklesIcon,
  StarIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import Reveal from '../components/Reveal';
import ProductCard from '../components/ProductCard';
import { ProductCardSkeleton } from '../components/Skeletons';

const FEATURES = [
  {
    icon: BoltIcon,
    title: 'Super cepat',
    description:
      'Data produk dimuat langsung dari API dengan pagination server-side, jadi halaman tetap ringan.',
    accent: 'from-amber-400 to-orange-500',
  },
  {
    icon: DevicePhoneMobileIcon,
    title: 'Mobile first',
    description:
      'Tampilan menyesuaikan diri dari layar 320px sampai desktop lebar tanpa kehilangan detail.',
    accent: 'from-sky-400 to-blue-600',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Data terpercaya',
    description:
      'Harga, stok, dan rating selalu sinkron dengan sumber data DummyJSON secara real-time.',
    accent: 'from-emerald-400 to-teal-600',
  },
];

const STATS = [
  { value: '194+', label: 'Produk aktif' },
  { value: '4.8', label: 'Rating rata-rata' },
  { value: '24/7', label: 'Selalu online' },
];

function HeroProduct({ product, className = '', delay = '' }) {
  if (!product) return null;

  return (
    <div
      className={`absolute overflow-hidden rounded-3xl border border-white/70 bg-white p-3 shadow-lift ${className}`}
    >
      <div className="overflow-hidden rounded-2xl bg-slate-100">
        <img
          src={product.thumbnail}
          alt={product.title}
          className="h-full w-full object-cover"
        />
      </div>
      <div className={`mt-3 px-1 pb-1 ${delay}`}>
        <p className="line-clamp-1 text-xs font-semibold text-slate-800">
          {product.title}
        </p>
        <div className="mt-1 flex items-center justify-between">
          <span className="text-sm font-bold text-slate-900">
            ${Number(product.price).toFixed(2)}
          </span>
          <span className="flex items-center gap-0.5 text-[11px] font-semibold text-amber-500">
            <StarIcon className="h-3 w-3" /> {Number(product.rating).toFixed(1)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    axios
      .get('/products?limit=8&skip=0')
      .then(({ data }) => {
        if (active) setFeatured(data.products || []);
      })
      .catch(() => {
        if (active) setFeatured([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const hero = featured.slice(0, 3);

  return (
    <div className="relative overflow-hidden bg-soft-gradient">
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute inset-0 dot-grid-bg opacity-60 [mask-image:radial-gradient(70%_60%_at_50%_0%,black,transparent)]" />
        <div className="absolute -left-32 top-10 h-72 w-72 animate-blob rounded-full bg-brand-300/40 blur-3xl" />
        <div className="absolute right-0 top-0 h-80 w-80 animate-blob rounded-full bg-fuchsia-300/30 blur-3xl [animation-delay:-6s]" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 animate-blob rounded-full bg-sky-300/30 blur-3xl [animation-delay:-11s]" />
      </div>

      {/* Hero */}
      <section className="container-page relative pb-16 pt-14 sm:pt-20 lg:pb-24 lg:pt-24">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-8">
          <div className="animate-fade-up">
            <span className="chip border border-brand-200/70 bg-white/80 text-brand-700 shadow-sm backdrop-blur">
              <SparklesIcon className="h-3.5 w-3.5" />
              New season · Koleksi 2026
            </span>

            <h1 className="mt-5 text-4xl font-extrabold leading-[1.1] text-slate-900 sm:text-5xl lg:text-6xl">
              Belanja lebih{' '}
              <span className="relative inline-block">
                <span className="text-gradient animate-gradient-pan bg-[length:200%_200%]">
                  menyenangkan
                </span>
                <svg
                  className="absolute -bottom-2 left-0 w-full text-brand-300"
                  viewBox="0 0 200 12"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 9C40 3 90 2 198 6"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                </svg>
              </span>{' '}
              tanpa ribet.
            </h1>

            <p className="mt-6 max-w-xl text-base leading-7 text-slate-600 sm:text-lg">
              Temukan produk pilihan, bandingkan harga, dan lihat detail lengkap
              dalam satu tempat. Tampilan bersih, cepat, dan nyaman dipakai dari
              HP maupun desktop.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <NavLink to="/icons" className="btn-primary group">
                Mulai belanja
                <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-1" strokeWidth={2.5} />
              </NavLink>
              <NavLink to="/list" className="btn-secondary">
                Lihat data produk
              </NavLink>
            </div>

            <dl className="mt-10 grid max-w-md grid-cols-3 gap-4 border-t border-slate-200/80 pt-6">
              {STATS.map((stat) => (
                <div key={stat.label}>
                  <dt className="text-2xl font-extrabold text-slate-900 sm:text-3xl">
                    {stat.value}
                  </dt>
                  <dd className="mt-0.5 text-xs font-medium text-slate-500 sm:text-sm">
                    {stat.label}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Product collage */}
          <div className="relative mx-auto h-[360px] w-full max-w-md animate-fade-in sm:h-[440px] lg:h-[500px] lg:max-w-none">
            <div className="absolute inset-6 rounded-[2.5rem] bg-brand-gradient opacity-20 blur-2xl" />

            <div className="absolute left-1/2 top-1/2 h-[280px] w-[220px] -translate-x-1/2 -translate-y-1/2 rotate-[-4deg] animate-float sm:h-[340px] sm:w-[270px]">
              {hero[0] ? (
                <HeroProduct product={hero[0]} className="!relative inset-auto h-full w-full" />
              ) : loading ? (
                <div className="h-full w-full rounded-3xl border border-white/70 bg-white/70 shadow-lift backdrop-blur">
                  <div className="skeleton m-3 h-[65%] rounded-2xl" />
                  <div className="space-y-2 px-4">
                    <div className="skeleton h-3 w-3/4" />
                    <div className="skeleton h-3 w-1/3" />
                  </div>
                </div>
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-3xl border border-white/70 bg-white/80 p-6 text-center shadow-lift backdrop-blur">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-gradient text-white">
                    <ShoppingBagIcon className="h-7 w-7" />
                  </span>
                  <p className="text-sm font-bold text-slate-800">Katalog siap dijelajahi</p>
                  <NavLink to="/icons" className="text-xs font-semibold text-brand-600 hover:text-brand-800">
                    Buka shop →
                  </NavLink>
                </div>
              )}
            </div>

            <div className="absolute left-0 top-6 hidden w-40 rotate-[-8deg] animate-float animation-delay-500 sm:block">
              {hero[1] && <HeroProduct product={hero[1]} className="!relative inset-auto w-full" />}
            </div>

            <div className="absolute bottom-4 right-0 hidden w-44 rotate-[7deg] animate-float animation-delay-1000 sm:block">
              {hero[2] && <HeroProduct product={hero[2]} className="!relative inset-auto w-full" />}
            </div>

            <div className="absolute bottom-2 left-4 hidden items-center gap-2 rounded-2xl border border-white/70 bg-white/90 px-3.5 py-2.5 shadow-soft backdrop-blur sm:flex">
              <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                <ShieldCheckIcon className="h-4 w-4" strokeWidth={2.5} />
              </span>
              <div className="leading-tight">
                <p className="text-[11px] font-bold text-slate-800">Transaksi aman</p>
                <p className="text-[10px] text-slate-500">Data terenkripsi</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="container-page relative pb-16 lg:pb-24">
        <div className="grid gap-5 sm:grid-cols-3">
          {FEATURES.map((feature, index) => (
            <Reveal key={feature.title} delay={index * 110}>
              <article className="group h-full rounded-3xl border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur transition duration-300 hover:-translate-y-1.5 hover:shadow-lift">
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${feature.accent} text-white shadow-lg transition duration-300 group-hover:scale-110 group-hover:rotate-3`}
                >
                  <feature.icon className="h-6 w-6" strokeWidth={1.8} />
                </span>
                <h2 className="mt-5 text-base font-bold text-slate-900">
                  {feature.title}
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {feature.description}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Popular products */}
      <section className="container-page relative pb-16 lg:pb-24">
        <Reveal className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600">
              Pilihan editor
            </p>
            <h2 className="mt-2 text-2xl font-extrabold text-slate-900 sm:text-3xl">
              Produk yang lagi populer
            </h2>
          </div>
          <NavLink
            to="/icons"
            className="group inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-800"
          >
            Lihat semua
            <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-1" strokeWidth={2.5} />
          </NavLink>
        </Reveal>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {loading
            ? Array.from({ length: 4 }).map((_, index) => (
                <ProductCardSkeleton key={index} />
              ))
            : featured.slice(0, 4).map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-page relative pb-20">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] bg-brand-gradient bg-[length:200%_200%] px-6 py-12 text-center animate-gradient-pan sm:px-12 sm:py-16">
            <div className="pointer-events-none absolute inset-0 dot-grid-bg opacity-20" aria-hidden="true" />
            <div className="relative mx-auto max-w-2xl">
              <h2 className="text-2xl font-extrabold text-white sm:text-4xl">
                Siap menemukan produk favoritmu?
              </h2>
              <p className="mt-3 text-sm leading-6 text-white/80 sm:text-base">
                Jelajahi katalog lengkap dengan pencarian instan dan pagination
                yang mulus. Gratis, tanpa perlu daftar.
              </p>
              <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
                <NavLink
                  to="/icons"
                  className="btn bg-white text-brand-700 hover:-translate-y-0.5 hover:bg-brand-50"
                >
                  Buka katalog
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={2.5} />
                </NavLink>
                <NavLink
                  to="/login"
                  className="btn border border-white/40 text-white hover:bg-white/10"
                >
                  Sign in
                </NavLink>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
